import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const STORAGE_KEY = "geovinum.assistant.session";

// WhatsApp deep-link — international format, no `+`, no spaces.
const WHATSAPP_NUMBER = "33660235665";

// Fully-localised chat greeting, subtitle and placeholder. Falls back to
// French for any unknown language so we never render an empty message.
const CHAT_I18N = {
  fr: {
    welcome:
      "Bonjour ! Je suis l'agent IA de geoVINUM 👋\n\nJe peux vous présenter l'app geoVINUM — explorer, déguster, apprendre, converser — ou vous aider à choisir une carte de vignoble en boutique. Que cherchez-vous ?",
    subtitle: "App · Boutique · Contenus",
    placeholder: "Posez votre question…",
    error: "Impossible de joindre l'assistant",
    whatsapp_aria: "Discuter sur WhatsApp",
    whatsapp_title: "Discuter sur WhatsApp · +33 6 60 23 56 65",
    whatsapp_text: "Bonjour, je viens de geovinum.io — j'aimerais en savoir plus sur l'app geoVINUM.",
  },
  en: {
    welcome:
      "Hi! I'm the geoVINUM app agent 👋\n\nI can walk you through the geoVINUM app — explore, taste, learn, chat — or help you pick a vineyard map in the shop. What are you after?",
    subtitle: "App · Shop · Content",
    placeholder: "Ask me anything…",
    error: "Could not reach the assistant",
    whatsapp_aria: "Chat on WhatsApp",
    whatsapp_title: "Chat on WhatsApp · +33 6 60 23 56 65",
    whatsapp_text: "Hi, I'm reaching out from geovinum.io — I'd like to know more about the geoVINUM app.",
  },
  es: {
    welcome:
      "¡Hola! Soy el agente de IA de geoVINUM 👋\n\nPuedo presentarte la app geoVINUM — explorar, catar, aprender, conversar — o ayudarte a elegir un mapa de viñedos en la tienda. ¿Qué buscas?",
    subtitle: "App · Tienda · Contenidos",
    placeholder: "Haz tu pregunta…",
    error: "No se puede contactar al asistente",
    whatsapp_aria: "Chatear por WhatsApp",
    whatsapp_title: "Chatear por WhatsApp · +33 6 60 23 56 65",
    whatsapp_text: "Hola, vengo de geovinum.io — me gustaría saber más sobre la app geoVINUM.",
  },
  pt: {
    welcome:
      "Olá! Sou o agente de IA da geoVINUM 👋\n\nPosso apresentar-lhe a app geoVINUM — explorar, degustar, aprender, conversar — ou ajudá-lo a escolher um mapa de vinhas na loja. Do que precisa?",
    subtitle: "App · Loja · Conteúdos",
    placeholder: "Faça a sua pergunta…",
    error: "Não foi possível contactar o assistente",
    whatsapp_aria: "Conversar no WhatsApp",
    whatsapp_title: "Conversar no WhatsApp · +33 6 60 23 56 65",
    whatsapp_text: "Olá, venho do geovinum.io — gostaria de saber mais sobre a app geoVINUM.",
  },
};

// Inline renderer: handles **bold**, [label](url), and strips stray markdown
// noise so the chat never shows raw `**` or `##` characters.
const renderRich = (raw) => {
  if (!raw) return null;
  // Strip Markdown noise we don't render explicitly.
  let text = String(raw)
    .replace(/^#{1,6}\s+/gm, "")          // headings
    .replace(/__([^_]+)__/g, "$1")        // alt-bold
    .replace(/(?<!\*)\*(?!\*)([^*\n]+)\*(?!\*)/g, "$1"); // single-* italics → plain

  const out = [];
  const rx = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let m;
  let key = 0;
  while ((m = rx.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      out.push(<strong key={`b-${key++}`}>{m[1]}</strong>);
    } else {
      out.push(
        <a
          key={`a-${key++}`}
          href={m[3]}
          target="_blank"
          rel="noreferrer"
          className="underline text-[color:var(--gv-pink)] hover:opacity-80"
        >
          {m[2]}
        </a>,
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

const ProductCard = ({ p }) => {
  const isSeries = p.kind === "academy_series" || (p.product_id || "").startsWith("series:");
  const href = isSeries
    ? `/academy/series/${(p.product_id || "").replace(/^series:/, "")}`
    : `/shop/${p.product_id}`;
  return (
  <a
    href={href}
    className="mt-2 flex items-center gap-3 p-2 bg-white border border-[color:var(--gv-line)] rounded-[5px] hover:border-[color:var(--gv-pink)]/60 transition-colors group"
    data-testid={isSeries ? "assistant-series-card" : "assistant-product-card"}
  >
    <div className="w-14 h-14 shrink-0 rounded-[5px] overflow-hidden bg-[color:var(--gv-mist)]">
      {p.preview ? (
        <img src={p.preview} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
      ) : null}
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-1.5">
        {isSeries && (
          <span className="px-1.5 py-0.5 bg-[color:var(--gv-blue)] text-white rounded-[3px] text-[9px] uppercase tracking-widest font-medium">
            Academy
          </span>
        )}
        <div className="text-[12.5px] font-medium text-[color:var(--gv-ink)] truncate group-hover:text-[color:var(--gv-pink)]">
          {p.title}
        </div>
      </div>
      <div className="text-[11px] text-[color:var(--gv-ink)]/55 truncate">{p.region}</div>
      <div className="text-[11.5px] text-[color:var(--gv-ink)]/80 mt-0.5">
        {isSeries ? p.from_price : <>dès <span className="font-semibold">{p.from_price}</span></>}
      </div>
    </div>
  </a>
  );
};

const AssistantWidget = () => {
  const lang = useLang();
  const L = CHAT_I18N[lang] || CHAT_I18N.fr;
  const WELCOME = L.welcome;
  const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(L.whatsapp_text)}`;
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  // Whenever the visitor switches language, refresh the welcome message
  // if the chat is still on its initial state (no user interaction yet).
  // If a conversation has already started we leave it alone.
  useEffect(() => {
    setMessages((prev) => {
      if (prev.length === 1 && prev[0].role === "assistant") {
        return [{ role: "assistant", content: WELCOME }];
      }
      return prev;
    });
  }, [WELCOME]);

  useEffect(() => {
    if (!open || !sessionId || messages.length > 1) return;
    (async () => {
      const { ok, data } = await apiFetch(`/api/assistant/messages/${sessionId}`);
      if (ok && data.messages?.length) {
        setMessages([{ role: "assistant", content: WELCOME }, ...data.messages]);
      }
    })();
  }, [open, sessionId, messages.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((prev) => [
      ...prev,
      { role: "user", content: text },
      { role: "assistant", content: "" },
    ]);
    setStreaming(true);
    const { ok, data: d, error: err } = await apiFetch(`/api/assistant/chat`, {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId || null, message: text }),
    });
    if (!ok) {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: "assistant",
          content: `⚠️ ${L.error} : ${err || "Erreur"}`,
        };
        return next;
      });
    } else {
      if (d.session_id && d.session_id !== sessionId) {
        setSessionId(d.session_id);
        localStorage.setItem(STORAGE_KEY, d.session_id);
      }
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: "assistant",
          content: d.reply || "",
          products: Array.isArray(d.products) ? d.products : [],
        };
        return next;
      });
    }
    setStreaming(false);
  };

  return (
    <>
      {/* Floating WhatsApp button — one stack level above the chat toggle
          so a human is always one tap away. Uses the official WhatsApp
          green so the affordance is instantly recognisable. Hidden when
          the chat panel is open to avoid crowding the corner. */}
      {!open && (
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={L.whatsapp_aria}
          className="fixed bottom-[5.25rem] right-5 z-50 h-14 w-14 rounded-[5px] shadow-lg flex items-center justify-center bg-[#25D366] text-white hover:scale-105 transition-transform"
          data-testid="whatsapp-toggle"
          title={L.whatsapp_title}
        >
          {/* Inline WhatsApp glyph — no extra lucide dependency. */}
          <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
            <path d="M20.52 3.48A11.94 11.94 0 0 0 12 0C5.37 0 .02 5.35.02 11.98c0 2.11.55 4.17 1.6 5.98L0 24l6.2-1.62a11.96 11.96 0 0 0 5.8 1.48h.01c6.62 0 11.98-5.35 11.98-11.98 0-3.2-1.24-6.2-3.47-8.4ZM12 21.79c-1.83 0-3.62-.49-5.17-1.42l-.37-.22-3.68.96.98-3.59-.24-.37a9.83 9.83 0 0 1-1.52-5.17c0-5.44 4.44-9.86 9.9-9.86 2.64 0 5.12 1.03 6.98 2.9a9.79 9.79 0 0 1 2.9 6.97c0 5.44-4.44 9.8-9.78 9.8Zm5.47-7.35c-.3-.15-1.77-.87-2.05-.97-.28-.1-.48-.15-.68.15-.2.3-.78.97-.96 1.17-.18.2-.35.22-.65.07-.3-.15-1.27-.47-2.42-1.5-.9-.8-1.5-1.78-1.68-2.08-.18-.3-.02-.46.13-.6.13-.13.3-.35.44-.53.15-.18.2-.3.3-.5.1-.2.05-.38-.02-.53-.07-.15-.68-1.63-.93-2.23-.24-.58-.5-.5-.68-.5l-.58-.01c-.2 0-.53.08-.8.38-.28.3-1.05 1.03-1.05 2.5s1.08 2.9 1.23 3.1c.15.2 2.13 3.25 5.15 4.55.72.31 1.28.5 1.72.63.72.23 1.38.2 1.9.12.58-.09 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.08-.13-.28-.2-.58-.35Z"/>
          </svg>
        </a>
      )}

      {/* Floating chat button */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="geoVINUM IA"
        className={`fixed bottom-5 right-5 z-50 h-14 w-14 rounded-[5px] shadow-lg flex items-center justify-center transition-all ${
          open ? "bg-[color:var(--gv-ink)] text-white" : "bg-[color:var(--gv-pink)] text-white hover:scale-105"
        }`}
        data-testid="assistant-toggle"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-24 right-5 z-50 w-[min(380px,calc(100vw-2.5rem))] h-[min(560px,calc(100vh-7rem))] bg-white border border-[color:var(--gv-line)] rounded-[5px] shadow-2xl flex flex-col overflow-hidden"
          data-testid="assistant-panel"
        >
          <div className="px-4 py-3 bg-[color:var(--gv-ink)] text-white flex items-center gap-2">
            <div className="w-8 h-8 rounded-[5px] bg-[color:var(--gv-pink)] flex items-center justify-center">
              <Sparkles size={14} />
            </div>
            <div>
              <div className="font-display text-[15px] leading-tight">geoVINUM IA</div>
              <div className="text-[11px] text-white/55">{L.subtitle}</div>
            </div>
          </div>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-[color:var(--gv-mist)]/40"
            data-testid="assistant-messages"
          >
            {messages.map((m, i) => (
              <div key={i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-[5px] text-[13.5px] leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[color:var(--gv-ink)] text-white"
                      : "bg-white border border-[color:var(--gv-line)] text-[color:var(--gv-ink)]"
                  }`}
                >
                  {m.content
                    ? renderRich(m.content)
                    : streaming && i === messages.length - 1
                      ? <Loader2 size={14} className="animate-spin opacity-50" />
                      : null}
                </div>
                {m.role === "assistant" && Array.isArray(m.products) && m.products.length > 0 && (
                  <div className="max-w-[85%] w-full mt-1">
                    {m.products.map((p) => (
                      <ProductCard key={p.product_id} p={p} />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="px-3 py-3 bg-white border-t border-[color:var(--gv-line)] flex items-center gap-2"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={L.placeholder}
              disabled={streaming}
              className="flex-1 h-10 px-3 rounded-[5px] border border-[color:var(--gv-line)] text-[13.5px] focus:border-[color:var(--gv-pink)]/40 focus:ring-1 focus:ring-[color:var(--gv-pink)]/20 outline-none"
              data-testid="assistant-input"
            />
            <button
              type="submit"
              disabled={streaming || !input.trim()}
              className="h-10 w-10 rounded-[5px] bg-[color:var(--gv-pink)] text-white flex items-center justify-center disabled:opacity-40"
              data-testid="assistant-send"
            >
              {streaming ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default AssistantWidget;
